// CONTRACT:C4-DESIGN-TOKENS.1.0
// COP — login screen (real idplite auth against the TDFLite single binary).
//
// Authenticates via POST /idp/token (password grant). The persona picker is
// populated from /api/dev-identities (the real users in policy.sealed.json). The
// logged-in identity's JWT claims become the ABAC subject that filters the picture.

import { useEffect, useState } from "react";
import { tdflite, type DevIdentity, type Session } from "../host/tdflite.ts";
import { BrandMark } from "./BrandMark.tsx";
import { pushToast } from "./Toasts.tsx";
import { personaFor, PERSONAS } from "../host/personas.ts";
import {
  beginGoogleSignIn,
  completeGoogleRedirect,
  isPlaceholderGoogleClient,
  readGoogleCallback,
} from "../host/google-oidc.ts";

// STATIC grid fallback (the "static personas.json" posture). /api/dev-identities is SEALED in prod
// (provision.sh) so the live grid would otherwise be empty; this derives the roster from the
// committed personas metadata instead. NO password is committed — a static-sourced persona PREFILLS
// the username (the operator types the password) rather than one-click logging in. Service NPEs
// (e.g. tak-gateway) are excluded — they are not human login identities. (CONTRACT:C4-DESIGN-TOKENS.1.0)
const STATIC_PERSONAS: DevIdentity[] = Object.entries(PERSONAS)
  .filter(([, meta]) => !meta.role.includes("NPE"))
  .map(([username, meta]) => ({ username, password: "", summary: `${meta.role} · ${meta.department}`, admin: false }));

export function LoginScreen({
  onLoggedIn,
  onBack,
}: {
  onLoggedIn: (s: Session) => void;
  /** Optional return-to-splash affordance (the Phase 3 entrance gate). Omitted ⇒ no Back link,
   *  so the original standalone login is unchanged. */
  onBack?: () => void;
}): JSX.Element {
  const [personas, setPersonas] = useState<DevIdentity[]>([]);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // DEV: the real roster (WITH passwords) from /api/dev-identities → one-click login. PROD: the
    // endpoint is SEALED so this 404s/empties → fall back to the static personas.json roster so the
    // grid is NEVER empty (clicking then prefills the username; see onClick below).
    tdflite
      .devIdentities()
      .then((rows) => setPersonas(rows.length > 0 ? rows : STATIC_PERSONAS))
      .catch(() => setPersonas(STATIC_PERSONAS));
  }, []);

  const doLogin = async (u: string, p: string): Promise<void> => {
    if (!u || !p) return;
    setBusy(u);
    setError(null);
    try {
      onLoggedIn(await tdflite.login(u, p));
    } catch (e) {
      setError(e instanceof Error ? e.message : "login failed");
      setBusy(null);
    }
  };

  // Complete a "Sign in with Google" redirect: if we landed back here with an
  // OAuth ?code (or ?error), hand it to the loop-safe completer. It strips the
  // OAuth params off the URL BEFORE doing anything (so a reload / re-render can
  // never re-fire it — the infinite-loop kill) and NEVER auto-retries on failure;
  // the code is exchanged for a TDFLite session SERVER-SIDE at the TRUSTED IdP.
  useEffect(() => {
    const cb = readGoogleCallback(window.location.href);
    if (!cb.code && !cb.error) return;
    setBusy("__google__");
    setError(null);
    void completeGoogleRedirect(cb, {
      clearUrl: () => window.history.replaceState({}, document.title, window.location.pathname),
      exchange: (c) => tdflite.federateGoogle(c),
    }).then((r) => {
      if ("session" in r) {
        onLoggedIn(r.session);
        return;
      }
      // Federation failed: surface the error and STOP — do not re-initiate sign-in.
      setError(r.error);
      setBusy(null);
    });
    // onLoggedIn is stable for the screen's lifetime; run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doGoogle = async (): Promise<void> => {
    setError(null);
    if (isPlaceholderGoogleClient()) {
      setError("Google sign-in needs a configured OAuth client id (VITE_GOOGLE_CLIENT_ID).");
      return;
    }
    setBusy("__google__");
    try {
      await beginGoogleSignIn(); // navigates away to Google
    } catch (e) {
      setError(e instanceof Error ? e.message : "could not start google sign-in");
      setBusy(null);
    }
  };

  return (
    <div className="login">
      <div className="login__card">
        {onBack && (
          <button type="button" className="login__back" onClick={onBack}>
            ← Entrance
          </button>
        )}

        <div className="login__brand">
          {/* Compact brand lockup — the sealed-object mark + Saira wordmark; same
              language as the splash, at functional density. */}
          <div className="login__brandline">
            <BrandMark className="login__mark" size={28} />
            <div className="login__brandtext">
              <div className="login__wordmark">TAK · TDF</div>
              <div className="login__wordmark-sub">Coalition COP</div>
            </div>
          </div>
          <h1 className="login__title">Sign in</h1>
          <div className="login__sub">
            Authenticated by the <strong>TDFLite</strong> identity provider. Your clearance shapes the picture.
          </div>
        </div>

        <form
          className="login__form"
          onSubmit={(e) => {
            e.preventDefault();
            void doLogin(username, password);
          }}
        >
          <input
            className="login__input"
            placeholder="username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            className="login__input"
            placeholder="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button className="login__submit" type="submit" disabled={busy !== null}>
            Sign in
          </button>
        </form>

        {/* Hairline divider — the credential form is primary; federated / smartcard
            routes sit below the fold line. */}
        <div className="login__divider" aria-hidden="true">
          <span className="login__divider-label">or</span>
        </div>

        <button
          type="button"
          className="login__cac login__google"
          onClick={() => void doGoogle()}
          disabled={busy !== null}
        >
          <svg className="login__google-g" viewBox="0 0 48 48" width="16" height="16" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
          Sign in with Google
        </button>

        <button
          type="button"
          className="login__cac"
          onClick={() =>
            pushToast({
              title: "CAC / PIV",
              body: "Smartcard auth is designed (AOA-CERT-AUTH) — cert→identity→ABAC. Wiring pending.",
              tone: "info",
            })
          }
        >
          Sign in with CAC / PIV
        </button>

        {/* DEV / DEMO personas — SECONDARY to the free-form credential form above. Collapsed by
            default so the screen reads as a real sign-in; expanding reveals the roster. Clicking a
            persona PREFILLS the username (static prod posture, operator types the password) or
            one-click logs in (dev /api/dev-identities). Auth is UNCHANGED — doLogin(username,
            password) → POST /idp/token keys off the raw username only; display names never auth. */}
        {personas.length > 0 && (
          <details className="login__personas-details">
            <summary className="login__personas-summary">dev · demo personas</summary>
            <div className="login__persona-grid">
              {personas.map((p) => {
                const meta = personaFor(p.username);
                return (
                  <button
                    key={p.username}
                    type="button"
                    className="login__persona"
                    disabled={busy !== null}
                    onClick={() => {
                      // One-click when we have the password (dev /api/dev-identities); otherwise
                      // (static-sourced, no committed secret) PREFILL the username and let the
                      // operator type the password into the form above.
                      if (p.password) void doLogin(p.username, p.password);
                      else setUsername(p.username);
                    }}
                  >
                    <span className="login__persona-name">
                      {meta?.displayName ?? p.username}
                      {p.admin && <span className="login__admin">admin</span>}
                    </span>
                    {meta?.role && <span className="login__persona-role">{meta.role}</span>}
                    <span className="login__persona-clearance">{p.summary}</span>
                    {busy === p.username && <span className="login__persona-busy">authenticating…</span>}
                  </button>
                );
              })}
            </div>
          </details>
        )}

        {error && <div className="login__error">⚠ {error}</div>}

        <div className="login__footline">TDF-secured · fail-closed by construction</div>
      </div>
    </div>
  );
}
